import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  UserGroupIcon,
  ChartBarIcon,
  SparklesIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  RocketLaunchIcon,
  BanknotesIcon,
  ArrowLeftIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../hooks/useAuth';
import { agentPaymentAPI } from '../services/api';
import PublicFooter from '../components/common/PublicFooter';

interface AgentPaymentConfig {
  enabled: boolean;
  amount: number;
  currency: string;
  description: string;
}

const BecomeAgentPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [paymentConfig, setPaymentConfig] = useState<AgentPaymentConfig | null>(null);
  const [loading, setLoading] = useState(true);

  const getCurrencySymbol = (currency: string) => {
    switch ((currency || '').toUpperCase()) {
      case 'GHS':
        return 'GH₵';
      case 'USD':
        return '$';
      case 'EUR':
        return '€';
      case 'GBP':
        return '£';
      default:
        return currency;
    }
  };

  useEffect(() => {
    fetchPaymentConfig();
  }, []);

  const fetchPaymentConfig = async () => {
    try {
      const response = await agentPaymentAPI.getConfig();
      const payload = response?.data as any;
      if (payload?.success) {
        setPaymentConfig(payload.data);
      }
    } catch (error) {
      // Fallback if network issue
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterAsAgent = () => {
    if (!isAuthenticated) {
      // Not logged in - redirect to register with agent role
      navigate('/register?role=agent');
    } else if (user?.role === 'agent') {
      // Already an agent
      navigate('/dashboard');
    } else {
      // Logged in user wants to become agent - go to agent payment
      navigate('/agent-payment', {
        state: {
          paymentInfo: paymentConfig,
          user: {
            username: user?.username,
            email: user?.email,
            phone: user?.phone,
          },
          upgradeFromUser: true,
        },
      });
    }
  };

  const benefits = [
    {
      icon: BanknotesIcon,
      title: 'Wholesale Pricing',
      description: 'Up to 20% discount on MTN, Telecel, and AirtelTigo bundles.',
      badge: 'Max Profit',
    },
    {
      icon: BoltIcon,
      title: 'Instant Delivery',
      description: 'Automated 24/7 delivery to customer numbers within seconds.',
      badge: 'Automated',
    },
    {
      icon: ChartBarIcon,
      title: 'Sales Dashboard',
      description: 'Track your customer transactions, margins, and wallet balances live.',
      badge: 'Live Analytics',
    },
    {
      icon: UserGroupIcon,
      title: 'Unlimited Reselling',
      description: 'Zero caps on volume. Resell to as many clients as you want.',
      badge: 'Zero Limits',
    },
  ];

  const steps = [
    {
      num: '01',
      title: 'Sign Up as Agent',
      desc: 'Create your account or log in with your existing profile.',
    },
    {
      num: '02',
      title: 'Activate Your Status',
      desc: 'Complete the one-time registration fee or claim free activation.',
    },
    {
      num: '03',
      title: 'Start Reselling & Earning',
      desc: 'Access exclusive wholesale pricing and earn profit on every sale.',
    },
  ];

  // If user is already an agent, show confirmation view
  if (isAuthenticated && user?.role === 'agent') {
    return (
      <div className="min-h-screen bg-[#f8faf9] dark:bg-navy-950 flex flex-col justify-between">
        <div className="flex-1 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-navy-900 rounded-3xl p-6 sm:p-8 max-w-md w-full text-center shadow-xl border border-gray-100 dark:border-navy-700/60"
          >
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-primary-50 dark:bg-primary-950/50 rounded-2xl flex items-center justify-center mx-auto mb-5 border border-primary-100 dark:border-primary-800/40">
              <CheckCircleIcon className="h-9 w-9 sm:h-10 sm:w-10 text-primary-500" />
            </div>
            <h2 className="text-xl sm:text-2xl font-extrabold text-navy-900 dark:text-white mb-2">
              You're Already an Agent! 🎉
            </h2>
            <p className="text-sm sm:text-base text-gray-600 dark:text-navy-300 mb-6">
              You already have full access to wholesale agent pricing, instant delivery, and reseller tools.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full py-3.5 bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 text-white font-semibold rounded-xl shadow-brand hover:shadow-brand-hover transition-all touch-target text-base"
            >
              Go to Dashboard
            </button>
          </motion.div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8faf9] dark:bg-navy-950 flex flex-col text-navy-900 dark:text-white">
      {/* Top Navigation */}
      <header className="sticky top-0 z-30 bg-white/90 dark:bg-navy-900/90 backdrop-blur-md border-b border-gray-100 dark:border-navy-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-gray-600 dark:text-navy-300 hover:text-navy-900 dark:hover:text-white transition-colors touch-target py-1.5 px-2 -ml-2 rounded-lg hover:bg-gray-100 dark:hover:bg-navy-800 text-sm font-medium"
            aria-label="Go back"
          >
            <ArrowLeftIcon className="h-4 w-4 sm:h-5 sm:w-5" />
            <span>Back</span>
          </button>

          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-xs">
              <img src="/logo-dark.svg" alt="" className="w-4 h-4" draggable={false} />
            </div>
            <div className="leading-none text-left">
              <span className="font-extrabold text-navy-900 dark:text-white text-sm sm:text-base">
                GH<span className="text-primary-500">Data</span>
              </span>
              <span className="text-xs text-gray-500 dark:text-navy-300 ml-1 font-medium">Agent Program</span>
            </div>
          </div>

          <div className="w-14 sm:w-16 flex justify-end">
            {isAuthenticated ? (
              <button
                onClick={() => navigate('/dashboard')}
                className="text-xs sm:text-sm font-semibold text-primary-600 dark:text-primary-400 hover:underline"
              >
                Dashboard
              </button>
            ) : (
              <button
                onClick={() => navigate('/login')}
                className="text-xs sm:text-sm font-semibold text-primary-600 dark:text-primary-400 hover:underline"
              >
                Sign In
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-8 pb-12 sm:pt-14 sm:pb-20">
        {/* Subtle decorative background gradient */}
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-primary-500/8 via-transparent to-transparent" />
        <div className="absolute top-0 right-1/2 translate-x-1/2 w-96 sm:w-[600px] h-64 bg-primary-500/10 rounded-full blur-3xl -z-10 pointer-events-none" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            {/* Pill Badge */}
            <div className="inline-flex items-center gap-1.5 px-3 py-1 sm:px-4 sm:py-1.5 rounded-full bg-primary-50 dark:bg-primary-950/60 border border-primary-200/60 dark:border-primary-800/60 text-primary-700 dark:text-primary-300 text-xs sm:text-sm font-semibold mb-4 sm:mb-6 shadow-xs">
              <SparklesIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary-500" />
              <span>Become an Official Data Reseller</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-2xl xs:text-3xl sm:text-4xl lg:text-5xl font-black text-navy-900 dark:text-white tracking-tight leading-tight sm:leading-tight mb-3 sm:mb-5">
              Start Your Data Business{' '}
              <span className="bg-gradient-to-r from-primary-500 to-primary-600 bg-clip-text text-transparent">
                in Ghana
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-sm sm:text-base lg:text-lg text-gray-600 dark:text-navy-200 max-w-xl mx-auto mb-6 sm:mb-8 leading-relaxed">
              Earn steady daily income by reselling wholesale data bundles for MTN, Telecel, and AirtelTigo with instant automated delivery.
            </p>

            {/* Pricing Highlight Card */}
            {!loading && paymentConfig?.enabled && (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="w-full max-w-xs sm:max-w-sm mx-auto bg-white dark:bg-navy-900 rounded-2xl p-4 sm:p-6 shadow-medium border border-gray-100 dark:border-navy-700/70 mb-6 sm:mb-8"
              >
                <span className="text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-navy-400">
                  Registration Fee
                </span>
                <div className="flex items-baseline justify-center gap-1 my-1.5">
                  {paymentConfig.amount === 0 ? (
                    <span className="text-3xl sm:text-4xl font-black text-primary-600 dark:text-primary-400">
                      FREE
                    </span>
                  ) : (
                    <span className="text-3xl sm:text-4xl font-black text-navy-900 dark:text-white">
                      {getCurrencySymbol(paymentConfig.currency)}{paymentConfig.amount.toFixed(2)}
                    </span>
                  )}
                </div>
                <div className="pt-3 mt-3 border-t border-gray-100 dark:border-navy-800 flex items-center justify-center gap-2 sm:gap-3 text-xs text-primary-600 dark:text-primary-400 font-semibold">
                  <span>✓ One-time fee</span>
                  <span>•</span>
                  <span>✓ Lifetime wholesale rates</span>
                </div>
              </motion.div>
            )}

            {/* CTA Button */}
            <div className="w-full max-w-sm mx-auto">
              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleRegisterAsAgent}
                disabled={loading}
                className="w-full py-3.5 sm:py-4 px-6 bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 text-white font-bold rounded-xl shadow-brand hover:shadow-brand-hover transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed text-base sm:text-lg touch-target"
              >
                <RocketLaunchIcon className="h-5 w-5" />
                <span>{loading ? 'Loading...' : 'Register as Agent'}</span>
                <ArrowRightIcon className="h-5 w-5" />
              </motion.button>
              <p className="text-xs text-gray-500 dark:text-navy-400 mt-2.5">
                Quick setup · Immediate wholesale access upon activation
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-10 sm:py-16 bg-white dark:bg-navy-900/50 border-y border-gray-100 dark:border-navy-800/80">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-xl mx-auto mb-8 sm:mb-12">
            <span className="text-xs sm:text-sm font-bold text-primary-600 dark:text-primary-400 uppercase tracking-widest">
              Agent Advantages
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-navy-900 dark:text-white mt-1">
              Why Partner With Us?
            </h2>
            <p className="text-sm text-gray-500 dark:text-navy-300 mt-2">
              Everything you need to launch and grow a lucrative data reselling business in one platform.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 * index }}
                className="bg-[#f8faf9] dark:bg-navy-900 rounded-2xl p-5 border border-gray-200/80 dark:border-navy-700/70 hover:border-primary-400 dark:hover:border-primary-500/50 hover:shadow-medium transition-all group flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-11 h-11 rounded-xl bg-primary-100 dark:bg-primary-950/60 text-primary-600 dark:text-primary-400 flex items-center justify-center group-hover:scale-105 transition-transform">
                      <benefit.icon className="h-6 w-6" />
                    </div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-950/60 px-2 py-0.5 rounded-md border border-primary-200/50 dark:border-primary-800/40">
                      {benefit.badge}
                    </span>
                  </div>
                  <h3 className="font-bold text-navy-900 dark:text-white mb-1.5 text-base sm:text-lg">
                    {benefit.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-navy-300 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works - Mobile Friendly Stepper */}
      <section className="py-10 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-xl mx-auto mb-8 sm:mb-12">
            <span className="text-xs sm:text-sm font-bold text-primary-600 dark:text-primary-400 uppercase tracking-widest">
              Simple Onboarding
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-navy-900 dark:text-white mt-1">
              How to Get Started
            </h2>
            <p className="text-sm text-gray-500 dark:text-navy-300 mt-2">
              Three fast steps to activate your agent privileges
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 relative">
            {steps.map((step, index) => (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                className="bg-white dark:bg-navy-900 rounded-2xl p-5 sm:p-6 border border-gray-100 dark:border-navy-700/70 shadow-soft relative flex flex-col items-center text-center"
              >
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 text-white font-black text-lg flex items-center justify-center mb-4 shadow-xs">
                  {step.num}
                </div>
                <h3 className="font-bold text-navy-900 dark:text-white mb-1.5 text-base sm:text-lg">
                  {step.title}
                </h3>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-navy-300 leading-relaxed">
                  {step.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final Brand CTA Banner */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-12 sm:pb-16 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-navy-900 via-navy-800 to-navy-950 rounded-2xl sm:rounded-3xl p-6 sm:p-10 text-center text-white relative overflow-hidden border border-navy-700/50 shadow-xl"
        >
          {/* Subtle background glow */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary-500/10 rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10 max-w-lg mx-auto">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-extrabold mb-2 text-white">
              Ready to Start Earning?
            </h2>
            <p className="text-xs sm:text-sm text-navy-200 mb-6 leading-relaxed">
              Activate your account now to unlock discounted wholesale data bundles and start delivering to customers today.
            </p>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleRegisterAsAgent}
              className="w-full sm:w-auto px-8 py-3.5 bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 text-white font-bold rounded-xl shadow-brand hover:shadow-brand-hover transition-all inline-flex items-center justify-center gap-2 text-sm sm:text-base touch-target cursor-pointer"
            >
              <RocketLaunchIcon className="h-5 w-5" />
              <span>Get Started Now</span>
            </motion.button>
          </div>
        </motion.div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default BecomeAgentPage;
